import {useRef} from 'react';

const LONG_PRESS_MS = 500;
const MOVE_TOLERANCE_PX = 10;

/**
 * Opens a context menu on a touch long press too: iOS Safari fires no `contextmenu` for one. Spread
 * the result on the element instead of passing `onContextMenu` itself. Where the browser does fire
 * `contextmenu` on a long press (Android), that one wins; either way the tap that ends the press
 * is swallowed, so it doesn't change the selection under the menu.
 */
export function useLongPress(onContextMenu?: (e: React.MouseEvent) => void) {
    const timer = useRef<number | undefined>(undefined);
    const start = useRef<{ x: number; y: number; target: EventTarget } | null>(null);
    const fired = useRef(false);

    if (!onContextMenu) return {};

    function cancel() {
        window.clearTimeout(timer.current);
        start.current = null;
    }

    return {
        onTouchStart(e: React.TouchEvent) {
            cancel();
            fired.current = false;
            if (e.touches.length !== 1) return;
            const touch = e.touches[0];
            start.current = {x: touch.clientX, y: touch.clientY, target: e.target};
            timer.current = window.setTimeout(() => {
                const point = start.current;
                if (!point) return;
                fired.current = true;
                onContextMenu({
                    clientX: point.x,
                    clientY: point.y,
                    target: point.target,
                    preventDefault: () => undefined,
                } as unknown as React.MouseEvent);
            }, LONG_PRESS_MS);
        },
        onTouchMove(e: React.TouchEvent) {
            const touch = e.touches[0];
            if (start.current && touch
                && Math.hypot(touch.clientX - start.current.x, touch.clientY - start.current.y) > MOVE_TOLERANCE_PX) {
                cancel();
            }
        },
        onTouchEnd(e: React.TouchEvent) {
            cancel();
            if (fired.current) e.preventDefault();
        },
        onTouchCancel: cancel,
        onContextMenu(e: React.MouseEvent) {
            if (start.current) {
                // The browser's own long-press menu event: let it open the menu instead of the timer.
                cancel();
                fired.current = true;
            } else if (fired.current) {
                e.preventDefault();
                return;
            }
            onContextMenu(e);
        },
    };
}
